import { Box } from "@mui/material";
import { Direction } from "../lazertrader/types";
import { usePrefillContext } from "../../Services/PrefillContextProvider";
import { memo } from "react";
import { WideOrderBookRow } from "./hooks";
import { formatMegawatts } from "../../megawatts";
import { formatYenPerKwh, Price } from "../../price";
import { green, red } from "@mui/material/colors";

type Props = {
  row: WideOrderBookRow;
  clusterId: string;
  direction: Direction;
};

const QuoteCellComponent = ({ row, clusterId, direction }: Props) => {
  const { updatePrefill } = usePrefillContext();
  const cell = row.cells[clusterId];

  if (cell === undefined) {
    return (
      <Box component="span" sx={{ color: "text.disabled" }}>
        ·
      </Box>
    );
  }

  // Not a button, and not the same glyph as an empty book: no member of this
  // cluster is accepting orders, so there is nothing to click through to. The
  // row exists at all only so the delivery window stops silently disappearing
  // from the ladder — see `useAllGridsOrderbook`.
  if (cell.suspended) {
    return (
      <Box
        component="span"
        title="Not open for trading"
        sx={{ color: "text.disabled", opacity: 0.45 }}
      >
        –
      </Box>
    );
  }

  const isBid = direction === Direction.BUY;
  const value = isBid ? cell.bestBid : cell.bestAsk;
  const isOwn = isBid ? cell.topBidIsOwn : cell.topAskIsOwn;
  const decoupled = isBid ? cell.bidDecoupled : cell.askDecoupled;
  const mw =
    value === undefined
      ? undefined
      : formatMegawatts(isBid ? cell.bidMw : cell.askMw);

  const onClick = () => {
    // Our own order's area first, so clicking a blue cell opens the depth (and
    // the ticket) where that order actually rests rather than in whichever
    // coupled area happens to come first.
    const topArea =
      (isBid ? cell.bidOwnArea : cell.askOwnArea) ??
      (isBid ? cell.bidArea : cell.askArea);

    // Open members only. A partially suspended cluster still quotes and is still
    // worth clicking, but offering a suspended area in the ticket's grid picker
    // would let a trader send an order the venue is certain to reject.
    const tradable = cell.members.filter((member) => member.openForTrading);

    const contractsByArea: Record<string, number> = {};
    const spotByArea: Record<string, Price | undefined> = {};
    for (const member of tradable) {
      contractsByArea[member.areaCode] = member.contractId;
      spotByArea[member.areaCode] = member.spot;
    }

    const tradableAreaCodes = tradable.map((member) => member.areaCode);

    updatePrefill({
      // The side that trades against the price you clicked: you sell into a bid
      // and buy from an ask.
      direction: isBid ? Direction.SELL : Direction.BUY,
      clickedSide: isBid ? "bid" : "ask",
      grid: topArea ?? tradableAreaCodes[0],
      gridOptions: tradableAreaCodes,
      contractsByArea,
      spotByArea,
      productType: cell.productDuration,
      product: cell.contractName,
      deliveryStart: row.contractDeliveryStartUtc,
      deliveryEnd: row.contractDeliveryEndUtc,
      price: value,
    });
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        appearance: "none",
        background: "none",
        border: 0,
        padding: 0,
        margin: 0,
        width: "100%",
        height: "100%",
        font: "inherit",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexDirection: isBid ? "row" : "row-reverse",
        gap: "3px",
        paddingX: "4px",
        "&:hover .quote-price": {
          textDecoration: "underline",
        },
      }}
    >
      <Box
        component="span"
        sx={{
          color: "warning.main",
          fontSize: "0.9em",
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {mw}
      </Box>
      <Box component="span" className="quote-price" sx={{ flexShrink: 0 }}>
        <Box
          component="span"
          sx={{
            fontWeight: isOwn ? "bold" : "normal",
            color: isOwn ? "primary.main" : isBid ? green[400] : red[400],
          }}
        >
          {formatYenPerKwh(value)}
        </Box>
        {decoupled && (
          <Box component="span" sx={{ color: "warning.main" }}>
            *
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const QuoteCell = memo(QuoteCellComponent);

QuoteCell.displayName = "QuoteCell";
